import { useRef, useState } from 'react';
import type { DragEvent, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonSize } from '../../Fundamentals/Button';
import EmptyState from '../../Fundamentals/EmptyState/EmptyState';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import SkynetIconButton from '../../Fundamentals/IconButton/SkynetIconButton';
import Spinner from '../../Fundamentals/Spinner/Spinner';
import PackageTierCard from './PackageTierCard';
import { reorderTiers, sortTiersByRank } from './tierUtils';
import type { PackageTier, TierRankChange } from './Packages.types';

interface PackageTiersTabProps {
    tiers: PackageTier[] | undefined;
    isLoading: boolean;
    onAdd?: () => void;
    onEdit?: (tier: PackageTier) => void;
    onDelete?: (tier: PackageTier) => void;
    onReorder?: (changes: TierRankChange[]) => void;
}

const PackageTiersTab = ({ tiers, isLoading, onAdd, onEdit, onDelete, onReorder }: PackageTiersTabProps) => {
    const { t } = useTranslation();
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

    const sortedTiers = sortTiersByRank(tiers ?? []);
    const canReorder = onReorder !== undefined && sortedTiers.length > 1;

    const move = (fromIndex: number, toIndex: number) => {
        if (toIndex < 0 || toIndex >= sortedTiers.length || toIndex === fromIndex) return;
        onReorder?.(reorderTiers(sortedTiers, fromIndex, toIndex));
    };

    const startDrag = (event: DragEvent, index: number) => {
        setDragIndex(index);
        event.dataTransfer.effectAllowed = 'move';
        // Firefox won't start a drag without data
        event.dataTransfer.setData('text/plain', sortedTiers[index].id ?? '');
        const card = cardsRef.current[index];
        if (card !== null && card !== undefined) event.dataTransfer.setDragImage(card, 24, 24);
    };

    const drop = (event: DragEvent, index: number) => {
        event.preventDefault();
        if (dragIndex !== null) move(dragIndex, index);
        setDragIndex(null);
    };

    const moveWithKeyboard = (event: KeyboardEvent, index: number) => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
        event.preventDefault();
        move(index, event.key === 'ArrowUp' ? index - 1 : index + 1);
    };

    const list = () => {
        if (tiers === undefined) return isLoading ? <Spinner /> : null;

        if (sortedTiers.length === 0) return <EmptyState icon="list-ordered" title={t('packages.noTiers')} />;

        return (
            <div className="d-flex flex-column gap-16">
                {sortedTiers.map((tier, index) => (
                    <div
                        key={tier.id}
                        ref={(element) => {
                            cardsRef.current[index] = element;
                        }}
                        onDragOver={(event) => {
                            if (dragIndex !== null) event.preventDefault();
                        }}
                        onDrop={(event) => drop(event, index)}
                    >
                        <PackageTierCard
                            tier={tier}
                            position={index + 1}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            dragHandle={
                                canReorder && (
                                    <span
                                        draggable
                                        onDragStart={(event) => startDrag(event, index)}
                                        onDragEnd={() => setDragIndex(null)}
                                        onKeyDown={(event) => moveWithKeyboard(event, index)}
                                    >
                                        <SkynetIconButton
                                            icon="menu"
                                            label={t('packages.moveTier', { name: tier.name })}
                                            title={t('packages.dragToReorder') ?? ''}
                                            size={ButtonSize.SMALL}
                                        />
                                    </span>
                                )
                            }
                        />
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            <div className="d-flex align-items-start justify-content-between gap-16 flex-wrap mb-16">
                <div>
                    <h3 className="heading-lg mb-4">{t('packages.tabTiers')}</h3>
                    <p className="text-secondary font-13 mb-0">{t('packages.tiersDescription')}</p>
                </div>
                {onAdd !== undefined && (
                    <Button className="flex-shrink-0" onClick={onAdd}>
                        <SpirisIcon name="plus" className="mr-8" />
                        {t('packages.addTier')}
                    </Button>
                )}
            </div>
            {list()}
        </>
    );
};

export default PackageTiersTab;
